import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Building2, ArrowLeft, CheckCircle } from "lucide-react";
import useAuthStore from "../store/authStore";
import { createTenant, updateUpworkOAuthCredentials } from "../services/tenantService";
import { AccessDeniedCard } from "./AccessDeniedCard";
import { AdminPageLayout } from "./AdminPageLayout";
import { AdminCard } from "./AdminCard";

export function TenantRegisterPage() {
    const { user } = useAuthStore();
    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: "",
        subdomain: "",
        organizationName: "",
        organizationEmail: "",
        upworkClientId: "",
        upworkClientSecret: "",
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        try {
            const tenantData = {
                name: form.name.trim(),
                subdomain: form.subdomain.trim().toLowerCase(),
                organizationName: form.organizationName.trim() || undefined,
                organizationEmail: form.organizationEmail.trim() || undefined,
            };

            const result = await createTenant(tenantData);
            const tenantId = result.data?._id;

            // Save OAuth credentials if provided
            if (tenantId && form.upworkClientId && form.upworkClientSecret) {
                try {
                    await updateUpworkOAuthCredentials(tenantId, {
                        clientId: form.upworkClientId.trim(),
                        clientSecret: form.upworkClientSecret.trim(),
                    });
                } catch (oauthError) {
                    console.error("Error saving OAuth credentials:", oauthError);
                    // Don't fail tenant creation if OAuth save fails, but warn user
                    setSuccess("Tenant created successfully, but OAuth credentials could not be saved. Please configure them in the Integrations section.");
                    setTimeout(() => navigate("/admin"), 2000);
                    return;
                }
            }

            setSuccess("Tenant created successfully. You can now connect your Upwork account in the Integrations section.");
            setTimeout(() => navigate("/admin"), 2000);
        } catch (err) {
            const message = err?.response?.data?.error || err?.message || "Failed to create tenant";
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    // Guard: owner only (basic client-side check; server enforces real guard)
    if (user && user.role !== "owner") {
        return <AccessDeniedCard message="Owner access only. You don't have permission to register tenants." />;
    }

    return (
        <AdminPageLayout>
            <AdminCard
                title="Tenant Registration"
                description="Create a new organization and optionally store Upwork OAuth credentials."
                bodyClassName="space-y-5"
                className="max-w-2xl"
            >
                <div className="flex items-start gap-3">
                    <div className="bg-primary-100 p-2.5 rounded-lg">
                        <Building2 className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">
                            Owner-only access. Make sure you have the correct Upwork OAuth credentials.
                        </p>
                    </div>
                </div>

                {error && <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">{error}</div>}
                {success && (
                    <div className="bg-primary-50 text-primary-700 p-3 rounded-md flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {success}
                    </div>
                )}

                <form onSubmit={onSubmit} className="space-y-5">
                    <div>
                        <label htmlFor="name" className="block text-xs font-medium text-gray-700">Name</label>
                        <input
                            id="name"
                            name="name"
                            type="text"
                            required
                            value={form.name}
                            onChange={onChange}
                            className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                    </div>

                    <div>
                        <label htmlFor="subdomain" className="block text-xs font-medium text-gray-700">Subdomain</label>
                        <input
                            id="subdomain"
                            name="subdomain"
                            type="text"
                            pattern="^[a-z0-9-]+$"
                            title="Lowercase letters, numbers, and hyphens only"
                            required
                            value={form.subdomain}
                            onChange={onChange}
                            className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                    </div>

                    <div>
                        <label htmlFor="organizationName" className="block text-xs font-medium text-gray-700">Organization Name</label>
                        <input
                            id="organizationName"
                            name="organizationName"
                            type="text"
                            value={form.organizationName}
                            onChange={onChange}
                            className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                    </div>

                    <div>
                        <label htmlFor="organizationEmail" className="block text-xs font-medium text-gray-700">Organization Email</label>
                        <input
                            id="organizationEmail"
                            name="organizationEmail"
                            type="email"
                            value={form.organizationEmail}
                            onChange={onChange}
                            className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                    </div>

                    <div className="border-t border-gray-200 pt-5 mt-5 space-y-4">
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900">Upwork Integration</h3>
                            <p className="text-xs text-gray-500 mt-1">
                                Required for job sync. Get credentials from{" "}
                                <a
                                    href="https://www.upwork.com/developer/keys"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-600 hover:text-primary-700 underline"
                                >
                                    Upwork Developer Keys
                                </a>{" "}
                                (select Key Type: OAuth 2.0).
                            </p>
                        </div>

                        <div>
                            <label htmlFor="upworkClientId" className="block text-xs font-medium text-gray-700">Upwork Client ID *</label>
                            <input
                                id="upworkClientId"
                                name="upworkClientId"
                                type="text"
                                required
                                value={form.upworkClientId}
                                onChange={onChange}
                                className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="Enter your Upwork OAuth Client ID"
                            />
                        </div>

                        <div>
                            <label htmlFor="upworkClientSecret" className="block text-xs font-medium text-gray-700">Upwork Client Secret *</label>
                            <input
                                id="upworkClientSecret"
                                name="upworkClientSecret"
                                type="password"
                                required
                                value={form.upworkClientSecret}
                                onChange={onChange}
                                className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="Enter your Upwork OAuth Client Secret"
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full flex justify-center py-2 px-4 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? "Creating tenant..." : "Create Tenant"}
                    </button>
                </form>

                <div>
                    <Link to="/admin" className="inline-flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium transition-colors">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Admin Panel
                    </Link>
                </div>
            </AdminCard>
        </AdminPageLayout>
    );
}

export default TenantRegisterPage;
